import { useEffect } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useAppDispatch } from "../store/hooks"
import { clearAccessToken, setAuthState } from "../store/authSlice"
import { expireMembership, loadMembership } from "../store/billingSlice"
import {
  clearPersistedAccessToken,
  configureAuthClient,
  getAccessTokenFromAuth,
  redirectToPublicApp,
} from "./authClient"
import { setAuthenticatedUser, clearAuthenticatedUser } from "../config/appInsights"
import { setSentryUser, clearSentryUser } from "../config/sentry"

const AuthBootstrap = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated, isLoading, user, error, loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0()

  // A failed or cancelled Auth0 login surfaces here as `error` — send the user
  // back to the public marketing app instead of leaving them on a broken screen.
  useEffect(() => { if (error) redirectToPublicApp() }, [error])

  useEffect(() => {
    configureAuthClient({ loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims })
  }, [loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims])

  useEffect(() => {
    dispatch(setAuthState({ isAuthenticated, isLoading, user: user || null }))
  }, [dispatch, isAuthenticated, isLoading, user])

  useEffect(() => {
    clearPersistedAccessToken()
    if (!isAuthenticated || !user?.sub) {
      dispatch(clearAccessToken())
      clearAuthenticatedUser()
      clearSentryUser()
      return
    }
    let stopped = false
    setAuthenticatedUser(user.sub)
    setSentryUser(user.sub, user.email)
    const handleRefresh = async () => {
      dispatch(expireMembership())
      try {
        const token = await getAccessTokenFromAuth()
        if (token && !stopped) await dispatch(loadMembership(undefined))
      } catch {}
    }
    const handleVisibilityChange = () => { if (document.visibilityState === "visible") void handleRefresh() }
    void handleRefresh()
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void handleRefresh() }, 30000)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [dispatch, isAuthenticated, user?.sub, user?.email])

  return null
}

export default AuthBootstrap
